import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Info, ArrowDownRight, ArrowUpRight, Minus, AlertCircle, BadgeCheck } from "lucide-react";
import { fmtAmt, fmtDate, freshness } from "@/lib/flags";

/** Tap on mobile, hover on desktop. Shows:
 *  - Same-vendor price change vs the last purchase from this supplier
 *  - Cheapest alternate vendor with rate + date
 */
export default function ItemInsightPopover({ item, supplierName }) {
  const [open, setOpen] = useState(false);

  const rate = Number(item.rate || 0);
  const lastRate = item.last_rate != null ? Number(item.last_rate) : null;
  const lastVdate = item.last_vdate;
  const pf = item.price_flag || {};
  const vf = item.vendor_flag || {};

  // Same-vendor delta
  let samePill = null;
  let samePillIcon = Minus;
  let samePillCls = "text-slate-600 bg-slate-100";
  let sameText = "No prior purchase from this vendor.";
  if (lastRate != null && lastRate > 0) {
    const delta = ((rate - lastRate) / lastRate) * 100;
    const direction = delta > 0.5 ? "up" : delta < -0.5 ? "down" : "flat";
    if (direction === "up") { samePillIcon = ArrowUpRight; samePillCls = "text-rose-700 bg-rose-100"; }
    else if (direction === "down") { samePillIcon = ArrowDownRight; samePillCls = "text-emerald-700 bg-emerald-100"; }
    samePill = `${delta > 0 ? "+" : ""}${delta.toFixed(1)}%`;
    sameText = direction === "flat"
      ? `Same rate as last purchase from this vendor (₹${fmtAmt(lastRate)}${lastVdate ? " on " + fmtDate(lastVdate) : ""}).`
      : `Last bought at ₹${fmtAmt(lastRate)}${lastVdate ? " on " + fmtDate(lastVdate) : ""} → now ₹${fmtAmt(rate)} (${samePill}).`;
  }  const SamePillIcon = samePillIcon;

  // Cheapest alternate vendor
  const altVendor = vf.lowest_vendor;
  const altRate = vf.lowest_rate != null ? Number(vf.lowest_rate) : null;
  const altVdate = vf.lowest_vdate;
  const altFresh = freshness(altVdate);
  const sameFresh = freshness(lastVdate);
  let altIsCurrent = false;
  if (supplierName && altVendor) {
    altIsCurrent = supplierName.trim().toLowerCase() === altVendor.trim().toLowerCase();
  }
  let altSavingPct = null;
  if (altRate != null && rate > 0 && altRate < rate && !altIsCurrent) {
    altSavingPct = ((rate - altRate) / rate) * 100;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Item insights"
          data-testid="item-insight-trigger"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onClick={() => setOpen((o) => !o)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          className="inline-flex items-center justify-center w-5 h-5 rounded-full text-cyan-700 hover:text-white hover:bg-cyan-600 transition-colors"
        >
          <Info size={12} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-[320px] p-3 text-xs"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <div className="space-y-3">
          {/* Header */}
          <div>
            <div className="font-semibold text-sm text-slate-900 truncate">{item.iname}</div>
            <div className="text-[10px] mono text-slate-400">{item.icode} · qty {item.qty} {item.unit}</div>
          </div>

          {/* Same vendor */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Same vendor</span>
              {samePill && (
                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${samePillCls}`}>
                  <SamePillIcon size={10} /> {samePill}
                </span>
              )}
            </div>
            <div className="text-slate-700 leading-snug">{sameText}</div>
            {sameFresh && lastRate != null && (
              <div className={`mt-1 inline-flex items-center gap-1 text-[10px] ${sameFresh.textClass}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${sameFresh.dotClass}`} />
                {sameFresh.tier === "fresh" ? "Fresh price" : sameFresh.tier === "ageing" ? "Ageing price" : "Stale price"} · {sameFresh.ago}
              </div>
            )}
          </div>

          {/* Alternate vendor */}
          <div className="pt-2 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Alternate vendors</div>
            {altIsCurrent ? (
              <div className="flex items-start gap-1.5 text-emerald-700">
                <BadgeCheck size={12} className="mt-0.5 shrink-0" />
                <span>This vendor is currently the cheapest on record at ₹{fmtAmt(altRate)}{altVdate ? ` (last seen ${fmtDate(altVdate)})` : ""}.</span>
              </div>
            ) : altVendor && altRate != null ? (
              <>
                <div className="text-slate-700 leading-snug">
                  Cheapest alternate: <b className="text-slate-900">{altVendor}</b> at <b className="num">₹{fmtAmt(altRate)}</b>
                  {altVdate && <span className="text-slate-400"> ({fmtDate(altVdate)})</span>}
                </div>
                {altFresh && (
                  <div className={`mt-1 inline-flex items-center gap-1 text-[10px] ${altFresh.textClass}`}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${altFresh.dotClass}`} />
                    {altFresh.tier === "fresh" ? "Fresh benchmark" : altFresh.tier === "ageing" ? "Ageing benchmark" : "Stale benchmark"} · {altFresh.ago}
                  </div>
                )}
                {altSavingPct != null && (
                  <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 text-[10px] font-bold">
                    <AlertCircle size={10} /> Save {altSavingPct.toFixed(1)}% by switching
                    {item.qty && altRate < rate && (
                      <span className="ml-1">(≈ ₹{fmtAmt((rate - altRate) * Number(item.qty || 0))})</span>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="text-slate-500 italic">No alternate vendor data — first purchase of this item.</div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
